import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import QuoteModal from "@/components/QuoteModal";
import SEO from "@/components/SEO";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HERO_IMAGE = "https://images.unsplash.com/photo-1504307651254-35680f356dfd?auto=format&fit=crop&w=2000&q=80";

export default function ProductsPage() {
  const [products, setProducts] = useState([]);
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("all");

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        await axios.post(`${API}/seed-products`);
        const response = await axios.get(`${API}/products`);
        setProducts(response.data);
      } catch (error) {
        console.error("Erreur lors du chargement des produits:", error);
      }
    };
    fetchProducts();
  }, []);

  const filteredProducts = activeTab === "all" 
    ? products 
    : products.filter(p => p.category === activeTab);

  return (
    <div className="min-h-screen" data-testid="products-page">
      <SEO 
        title="Produits - Agglos, Parpaings, Granulats de Silex"
        description="Catalogue AGRABAT: agglos 10x20x40, 15x20x40, 20x20x40, parpaings, briques, hourdis, granulats de silex 0/3, 3/8, 8/16. Prix compétitifs au Sénégal."
        keywords="agglos prix Sénégal, parpaing Thiès, granulats silex, brique béton, hourdis, cailloux construction, roche BTP, matériaux Dakar"
        url="https://agrabat.sn/produits"
      />
      {/* Hero Section */}
      <section
        className="relative h-[50vh] flex items-center justify-center"
        style={{
          backgroundImage: `url(${HERO_IMAGE})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        data-testid="products-hero"
      >
        <div className="absolute inset-0 bg-[#0A1320]/80" />
        <div className="relative z-10 text-center px-4">
          <p className="text-[#FFD100] font-medium uppercase tracking-widest mb-4">
            Notre Catalogue
          </p>
          <h1 className="font-oswald text-5xl md:text-7xl font-bold text-white uppercase tracking-tight">
            Nos Produits
          </h1>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-24 bg-white" data-testid="products-list-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Filter Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-12">
            <TabsList className="bg-slate-100 rounded-none p-1 w-full md:w-auto">
              <TabsTrigger 
                value="all" 
                className="rounded-none px-8 py-3 font-medium uppercase tracking-wider data-[state=active]:bg-[#1E3A5F] data-[state=active]:text-white"
                data-testid="tab-all"
              >
                Tous
              </TabsTrigger>
              <TabsTrigger 
                value="granulats" 
                className="rounded-none px-8 py-3 font-medium uppercase tracking-wider data-[state=active]:bg-[#1E3A5F] data-[state=active]:text-white"
                data-testid="tab-granulats"
              >
                Granulats
              </TabsTrigger>
              <TabsTrigger 
                value="agglos" 
                className="rounded-none px-8 py-3 font-medium uppercase tracking-wider data-[state=active]:bg-[#1E3A5F] data-[state=active]:text-white"
                data-testid="tab-agglos"
              >
                Agglos
              </TabsTrigger>
            </TabsList>

            <TabsContent value={activeTab} className="mt-8">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredProducts.map((product) => (
                  <div
                    key={product.id}
                    className="product-card group bg-white border border-slate-200 hover:border-[#FFD100] transition-all duration-300"
                    data-testid={`product-item-${product.id}`}
                  >
                    <div className="aspect-[4/3] overflow-hidden">
                      <img
                        src={product.image_url}
                        alt={product.name}
                        className="product-image w-full h-full object-cover"
                      />
                    </div>
                    <div className="p-6">
                      <span className="inline-block px-3 py-1 text-xs bg-[#FFD100]/10 text-[#FFD100] uppercase tracking-widest font-medium mb-3">
                        {product.category === "granulats" ? "Granulats" : "Agglos"}
                      </span>
                      <h3 className="font-oswald text-xl font-semibold text-[#1E3A5F] uppercase">
                        {product.name}
                      </h3>
                      <p className="text-slate-600 text-sm mt-3 leading-relaxed">
                        {product.description}
                      </p>
                      {product.specifications && (
                        <div className="mt-4 pt-4 border-t border-slate-200">
                          <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">
                            Spécifications
                          </p>
                          <p className="text-sm text-[#1E3A5F] font-medium">
                            {product.specifications}
                          </p>
                        </div>
                      )}
                      <Button
                        onClick={() => setIsQuoteModalOpen(true)}
                        className="w-full mt-6 bg-[#1E3A5F] text-white hover:bg-[#162B47] rounded-none py-3 font-bold uppercase tracking-wider"
                        data-testid={`product-quote-btn-${product.id}`}
                      >
                        Demander un Devis
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>
          </Tabs>

          {filteredProducts.length === 0 && (
            <div className="text-center py-12">
              <p className="text-slate-500">Chargement des produits...</p>
            </div>
          )}
        </div>
      </section>

      {/* Info Section */}
      <section className="py-24 bg-slate-50" data-testid="products-info-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Granulats Info */}
            <div>
              <span className="text-[#FFD100] font-medium uppercase tracking-widest">
                Production SILIKA GRANULATS
              </span>
              <h2 className="font-oswald text-3xl md:text-4xl font-bold text-[#1E3A5F] uppercase tracking-tight mt-4 mb-6">
                Granulats de Silex
              </h2>
              <p className="text-slate-600 leading-relaxed mb-6">
                Nos granulats de silex sont extraits et produits sur notre carrière partenaire SILIKA GRANULATS à Darou Khoudoss. 
                La qualité exceptionnelle du silex sénégalais garantit une résistance optimale pour 
                tous vos travaux de construction.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-[#FFD100] mt-2 flex-shrink-0" />
                  <span className="text-slate-600">Granulométries disponibles: 0/3, 3/8, 8/16</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-[#FFD100] mt-2 flex-shrink-0" />
                  <span className="text-slate-600">Idéal pour bétons, mortiers et travaux routiers</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-[#FFD100] mt-2 flex-shrink-0" />
                  <span className="text-slate-600">Livraison en vrac ou big-bag selon vos besoins</span>
                </li>
              </ul>
            </div>

            {/* Agglos Info */}
            <div>
              <span className="text-[#FFD100] font-medium uppercase tracking-widest">
                Production Thiès
              </span>
              <h2 className="font-oswald text-3xl md:text-4xl font-bold text-[#1E3A5F] uppercase tracking-tight mt-4 mb-6">
                Agglos
              </h2>
              <p className="text-slate-600 leading-relaxed mb-6">
                Notre unité de production d'agglos située à la sortie N°2 de l'autoroute de Thiès 
                fabrique des blocs de béton de haute qualité pour vos projets de construction.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-[#FFD100] mt-2 flex-shrink-0" />
                  <span className="text-slate-600">Agglos creux: 10x20x40, 15x20x40, 20x20x40</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-[#FFD100] mt-2 flex-shrink-0" />
                  <span className="text-slate-600">Agglos plein: 15x20x40</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-[#FFD100] mt-2 flex-shrink-0" />
                  <span className="text-slate-600">Hourdis: 16x20x40</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-[#FFD100] mt-2 flex-shrink-0" />
                  <span className="text-slate-600">Fabrication locale de qualité supérieure</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <QuoteModal isOpen={isQuoteModalOpen} onClose={() => setIsQuoteModalOpen(false)} />
    </div>
  );
}
